/* The Neon Factory - Google Reviews side tab, 1.1.0 (23 Sep 2026)
   ------------------------------------------------------------------
   1.1.0: on every screen size (1.0.0 was <= 1024 px only), the placement
   measures the bottom toolbar instead of assuming one (desktop has none), and
   the chat launcher is left entirely alone.

   The Elfsight Google Reviews badge used to float over the page - over the
   sign preview, the creator's controls and product buttons. It is replaced by
   a compact white tab on the left edge, labelled "Google Reviews". The tab starts collapsed; tapping it (or
   Enter / Space) opens the widget's OWN reviews panel - the real reviews,
   rating and count, white and full screen, with its own close button (and
   Escape). Closing the panel returns to the tab and puts focus back on it.

   Nothing is duplicated: the panel is opened by clicking the existing badge,
   which stays in the page (hidden by CSS on these widths only). If the widget
   has not loaded, no tab is shown and nothing is hidden. If a tap ever fails
   to open the panel, the tab removes itself and the original badge comes back.

   Like the chat guard, the tab steps aside while the page scrolls and, when
   scrolling settles, is placed at the height nearest the middle of the screen
   where it touches no button, link, field or the creator's sign preview.
*/
(function () {
  'use strict';

  var SETTLE_MS = 170;
  var CLEARANCE = 6;        // above and below the tab
  var SIDE_GAP  = 2;        // beside it: the page's own gutter already separates it from content
  var TOP_MIN   = 72;
  var STEP      = 12;
  var PROTECT = [
    'a[href]', 'button', 'input:not([type="hidden"])', 'select', 'textarea', 'label', 'summary',
    '[role="button"]', '[role="link"]', '[role="option"]', '[tabindex="0"]',
    '#tnf-stagewrap', '.halo-sticky-toolbar-mobile'
  ].join(',');
  /* never covered, even partly: purchase buttons, the sign preview, the chat and the bottom toolbar */
  var CRITICAL = [
    '#tnf-add', '#tnf-stagewrap', '.halo-sticky-toolbar-mobile',
    '[name="add"]', 'form[action*="/cart/add"] button', '.btn-addToCart', '.button-addToCart', 'a.button-ATC',
    '#cart-checkout', '#cart-sidebar-checkout', '.button-checkout', '.card-action a', '.card-action button'
  ].join(',');

  var html = document.documentElement;
  var tab = null, openedByTab = false, settleTimer = 0, polls = 0, portalObs = null, portalNode = null, watchTimer = 0, state = '', gaveUp = false;

  function inScope() { return true; }
  /* the top of whatever fixed bar sits on the bottom edge (the phone toolbar); the window's bottom otherwise */
  function bottomLimit(vh) {
    var tb = document.querySelector('.halo-sticky-toolbar-mobile'), lim = vh;
    if (tb) { var r = tb.getBoundingClientRect(), cs = getComputedStyle(tb); if (r.height > 0 && cs.display !== 'none' && cs.visibility !== 'hidden' && r.top < vh) lim = Math.min(lim, r.top); }
    return lim;
  }
  function badgeBox() { return document.querySelector('.es-badge-container'); }
  function clickTarget() {
    var b = badgeBox();
    return b ? (b.querySelector('.es-card-badge-container') || b.firstElementChild || b) : null;
  }
  function popupOpen() { return !!document.querySelector('.es-popup-container'); }

  function setState(s) {
    if (s === state) return;
    state = s;
    if (s) html.setAttribute('data-tnf-rt', s); else html.removeAttribute('data-tnf-rt');
  }

  /* rating and count, read from the widget itself so they are never stale */
  function readRating() {
    var b = badgeBox(); if (!b) return null;
    var v = b.querySelector('.es-rating-value');
    var rating = v ? v.textContent.trim() : '';
    var count = '';
    /* the count: the widget's own count element, else its schema.org AggregateRating, else "(77)" */
    var c = b.querySelector('.es-badge-reviews-count');
    var m = c && c.textContent.match(/\d[\d,]*/);
    if (m) count = m[0];
    if (!count) {
      var ld = document.querySelector('.es-portal-root script[type="application/ld+json"]');
      try { var j = ld && JSON.parse(ld.textContent); if (j && j.aggregateRating && j.aggregateRating.reviewCount) count = String(j.aggregateRating.reviewCount); } catch (e) {}
    }
    if (!count) { m = (b.textContent || '').match(/\((\d[\d,]*)\)/); if (m) count = m[1]; }
    return { rating: rating, count: count };
  }

  var G_LOGO = '<svg class="tnf-rt-g" viewBox="0 0 48 48" aria-hidden="true" focusable="false">' +
    '<path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>' +
    '<path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>' +
    '<path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>' +
    '<path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>' +
    '</svg>';

  /* the widget renders its rating a moment after the badge itself, so this runs again on every change */
  var shownRating = null;
  function label() {
    if (!tab) return;
    var r = readRating();
    if (!r || !/^\d/.test(r.rating)) return;
    var key = r.rating + '|' + r.count;
    if (key === shownRating) return;
    shownRating = key;
    tab.setAttribute('aria-label', 'Google Reviews, rated ' + r.rating + ' out of 5' +
      (r.count ? ' from ' + r.count + ' reviews' : '') + '. Open the reviews');
    var el = tab.querySelector('.tnf-rt-rating');
    if (!el) { el = document.createElement('span'); el.className = 'tnf-rt-rating'; el.setAttribute('aria-hidden', 'true'); tab.appendChild(el); }
    el.innerHTML = r.rating.replace(/[^0-9.,]/g, '') + ' <b>&#9733;</b>';
    place();
  }

  function build() {
    if (tab || gaveUp) return;
    html.classList.add('tnf-rt-on');
    tab = document.createElement('button');
    tab.type = 'button';
    tab.className = 'tnf-rt-tab';
    tab.setAttribute('aria-haspopup', 'dialog');
    tab.setAttribute('aria-expanded', 'false');
    tab.setAttribute('aria-label', 'Google Reviews. Open the reviews');
    tab.innerHTML = G_LOGO + '<span class="tnf-rt-label">Google Reviews</span>';
    tab.addEventListener('click', openPanel);
    /* Enter and Space open it directly (and the browser's own activation is suppressed, so it cannot open twice) */
    tab.addEventListener('keydown', function (e) {
      var k = e.key, c = e.code, n = e.keyCode;
      if (k === 'Enter' || c === 'Enter' || c === 'NumpadEnter' || n === 13 || k === ' ' || k === 'Spacebar' || c === 'Space' || n === 32) {
        e.preventDefault(); if (!e.repeat) openPanel();
      }
    });
    tab.addEventListener('keyup', function (e) { if (e.key === ' ' || e.key === 'Spacebar' || e.code === 'Space' || e.keyCode === 32) e.preventDefault(); });
    document.body.appendChild(tab);
    shownRating = null;
    label();
    place();
  }

  function unbuild(showBadge) {
    if (tab && tab.parentNode) tab.parentNode.removeChild(tab);
    tab = null;
    if (showBadge) html.classList.remove('tnf-rt-on');
    setState('');
  }

  var opening = false;
  function openPanel() {
    if (opening || state === 'open' || popupOpen()) return;
    var t = clickTarget();
    if (!t) { unbuild(true); return; }
    openedByTab = true;
    opening = true;
    t.click();
    watchPanel(true);
  }

  /* Follows one opening of the panel: waits for it to appear, then for it to close. Runs only while a
     panel is opening or open. If a tap on the tab gets no panel within 4 s, the tab removes itself and
     the widget's own badge comes back, so the reviews can always be opened. */
  function watchPanel(fromTab) {
    clearInterval(watchTimer);
    var seen = false, t0 = Date.now();
    watchTimer = setInterval(function () {
      var open = popupOpen();
      if (open && !seen) { seen = true; opening = false; sync(); }
      else if (!open && seen) { clearInterval(watchTimer); sync(); }
      else if (!open && !seen && Date.now() - t0 > 4000) {
        clearInterval(watchTimer); opening = false;
        if (fromTab) { openedByTab = false; gaveUp = true; unbuild(true); }
      }
    }, 150);
  }

  function sync() {
    if (!tab) return;
    if (popupOpen()) {
      tab.setAttribute('aria-expanded', 'true');
      setState('open');
    } else if (state === 'open') {
      tab.setAttribute('aria-expanded', 'false');
      setState('');
      place();
      if (openedByTab) {
        openedByTab = false;
        var a = document.activeElement;
        if (!a || a === document.body || !a.isConnected) { try { tab.focus({ preventScroll: true }); } catch (e) { tab.focus(); } }
      }
    }
  }

  function overlap(box, rects) {
    var total = 0;
    for (var i = 0; i < rects.length; i++) {
      var r = rects[i];
      if (r.bottom < box.top || r.top > box.bottom) continue;
      var w = Math.min(r.right, box.right) - Math.max(r.left, box.left);
      var h = Math.min(r.bottom, box.bottom) - Math.max(r.top, box.top);
      if (w > 0 && h > 0) total += w * h;
    }
    return total;
  }

  /* the height nearest the preferred one where a tab of height th touches nothing; failing that, the one
     that touches no purchase control / preview / chat / toolbar and least of anything else */
  function spot(th, all, crit, vh, reach) {
    var lo = TOP_MIN, hi = bottomLimit(vh) - 8 - th;
    if (hi < lo) hi = lo;
    var pref = Math.round(vh * 0.42 - th / 2), best = null, bestArea = Infinity, bestCrit = true;
    for (var d = 0; d <= vh; d += STEP) {
      var cands = d ? [pref + d, pref - d] : [pref];
      for (var j = 0; j < cands.length; j++) {
        var y = cands[j];
        if (y < lo || y > hi) continue;
        var box = { left: 0, right: reach, top: y - CLEARANCE, bottom: y + th + CLEARANCE };
        var a = overlap(box, all);
        if (a === 0) return { y: y, area: 0, crit: false };
        var c = overlap(box, crit) > 0;
        if ((bestCrit && !c) || (c === bestCrit && a < bestArea)) { best = y; bestArea = a; bestCrit = c; }
      }
    }
    return { y: best === null ? lo : best, area: bestArea, crit: bestCrit };
  }

  function rectsOf(sel, vh, reach) {
    var out = [], els = document.querySelectorAll(sel);
    for (var i = 0; i < els.length; i++) {
      var e = els[i];
      if (e === tab || tab.contains(e) || (e.closest && e.closest('.es-portal-root'))) continue;
      var r = e.getBoundingClientRect();
      if (!r.width || !r.height || r.left > reach || r.right < 0 || r.bottom < 0 || r.top > vh) continue;
      out.push(r);
    }
    return out;
  }

  /* The chat launcher itself, read (never changed) the way tnf-chat-guard reads it. The <shopify-chat> host
     element can span the whole window on desktop, so its own box says nothing about where the button is. */
  function chatRects(vh, reach) {
    var out = [];
    try {
      var h = document.querySelector('shopify-chat');
      var root = h && h.shadowRoot, b = root && root.querySelector('button.activator');
      if (!b && root) { var rt = root.querySelector('shopify-agent-runtime'); b = rt && rt.shadowRoot && rt.shadowRoot.querySelector('button.activator'); }
      if (b) { var r = b.getBoundingClientRect(); if (r.width && r.height && r.left <= reach && r.bottom >= 0 && r.top <= vh) out.push(r); }
    } catch (e) {}
    return out;
  }

  function show(y, compact) {
    tab.classList.toggle('is-compact', compact);
    tab.style.setProperty('--tnf-rt-y', y + 'px');
    if (state === 'moving' || state === 'tucked') setState('');
  }

  function place() {
    if (!tab || !inScope() || state === 'open') return;
    var vh = window.innerHeight, reach = (tab.offsetWidth || 32) + SIDE_GAP;
    var chat = chatRects(vh, reach);
    var all = rectsOf(PROTECT, vh, reach).concat(chat), crit = rectsOf(CRITICAL, vh, reach).concat(chat);
    var wasCompact = tab.classList.contains('is-compact');
    tab.classList.remove('is-compact');
    var thFull = tab.offsetHeight;
    if (!thFull) { tab.classList.toggle('is-compact', wasCompact); return; }   // not laid out yet: the idle check retries
    var full = spot(thFull, all, crit, vh, reach);
    if (full.area === 0) { show(full.y, false); return; }
    tab.classList.add('is-compact');
    var small = spot(tab.offsetHeight || 36, all, crit, vh, reach);
    if (!small.crit) { show(small.y, true); return; }
    if (!full.crit) { show(full.y, false); return; }
    /* nowhere on screen avoids a purchase control or the preview: step aside until the page moves */
    setState('tucked');
  }

  function onMove() {
    if (!tab || !inScope() || state === 'open') return;
    clearTimeout(settleTimer);
    if (tab.contains(document.activeElement)) { settleTimer = setTimeout(place, SETTLE_MS); return; }
    setState('moving');
    /* wait for scrolling to stop, then for the creator's pinned preview to finish resizing (220 ms) */
    settleTimer = setTimeout(function () { settleTimer = setTimeout(place, 260); }, SETTLE_MS);
  }

  /* the widget re-creates its portal after it first renders, so the observer follows whichever portal
     currently holds the badge */
  function watchPortal() {
    var b = badgeBox();
    var portal = (b && b.closest && b.closest('.es-portal-root')) || document.querySelector('.es-portal-root');
    if (!portal || portal === portalNode) return;
    if (portalObs) portalObs.disconnect();
    portalNode = portal;
    var queued = false;
    portalObs = new MutationObserver(function () {
      if (queued) return; queued = true;
      requestAnimationFrame(function () {
        queued = false;
        if (!badgeBox() && !popupOpen()) {
          /* the widget may be re-creating its portal: look again before giving the badge back */
          setTimeout(function () {
            if (badgeBox() || popupOpen()) { watchPortal(); if (!tab && badgeBox()) build(); label(); return; }
            if (tab) unbuild(true);
          }, 1500);
          return;
        }
        if (!tab && badgeBox()) build();
        label();
        sync();
      });
    });
    portalObs.observe(portal, { childList: true, subtree: true });
  }

  /* the page keeps laying out after the tab first appears (images, fonts, the creator's preview), so the
     tab is placed again whenever the page's size changes - never while it is scrolling or the panel is open */
  var layoutObs = null;
  function watchLayout() {
    if (layoutObs || !window.ResizeObserver) return;
    var queued = false;
    layoutObs = new ResizeObserver(function () {
      if (queued) return; queued = true;
      requestAnimationFrame(function () { queued = false; if (state === '' || state === 'tucked') place(); });
    });
    layoutObs.observe(document.body);
    window.addEventListener('load', function () { if (state === '' || state === 'tucked') place(); });
    /* and a light re-check while the page is idle: parts of the page (the creator's preview, images, the widget)
       finish laying out without changing the page's size. The tab only moves if its best place has changed. */
    setInterval(function () {
      if (!tab || document.hidden || tab.contains(document.activeElement)) return;
      if (state === '' || state === 'tucked') place();
    }, 2000);
  }

  function look() {
    if (badgeBox()) { build(); watchPortal(); watchLayout(); [800, 1800, 4000, 8000].forEach(function (ms) { setTimeout(function () { watchPortal(); label(); place(); }, ms); }); return; }
    if (++polls < 120) setTimeout(look, 500);    // the widget loads late; stop looking after a minute
  }

  function start() {
    html.classList.add('tnf-rt-on');
    document.addEventListener('click', function (e) {
      var t = e.target;
      if (t && t.closest && t.closest('.es-badge-container') && !openedByTab) { watchPortal(); watchPanel(false); }
    }, true);
    window.addEventListener('scroll', onMove, { passive: true });
    window.addEventListener('resize', function () { clearTimeout(settleTimer); settleTimer = setTimeout(place, SETTLE_MS); });
    window.addEventListener('orientationchange', function () { setTimeout(place, 300); });
    look();
  }

  try {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
  } catch (e) {
    html.classList.remove('tnf-rt-on');
  }
})();
